<template>
  <v-menu ref="menu" v-model="open" offset-y left :close-on-content-click="false" nudge-bottom="6">
    <template v-slot:activator="{ on, attrs }">
      <v-btn outlined class="column-btn" v-bind="attrs" v-on="on" aria-label="Alegeți coloanele">
        <v-icon :left="!isMobile">mdi-view-column-outline</v-icon>
        <template v-if="!isMobile">Coloane</template>
      </v-btn>
    </template>
    <div class="columns-menu">
      <div class="columns-menu__head">
        <div class="columns-menu__title">Coloane afișate</div>
        <v-btn v-if="prefKey && customized" text small color="primary" class="columns-menu__reset" @click="reset">
          <v-icon left small>mdi-restore</v-icon>Implicit
        </v-btn>
      </div>
      <div class="columns-checkboxes">
        <v-checkbox
          v-for="header in headers"
          :key="header.value"
          v-model="header.visible"
          :label="header.text"
          dense
          hide-details
          color="primary"
          @change="changed"
        ></v-checkbox>
      </div>
      <div v-if="prefKey" class="columns-menu__note">Alegerea se păstrează în contul dvs., pe orice dispozitiv.</div>
    </div>
  </v-menu>
</template>

<script>
import { getPreference, savePreference } from "@/helper/preferences";

/**
 * „Coloane”: which columns a table shows. With `prefKey` the choice is kept on the account (helper/preferences,
 * key columns.<prefKey>) as { shown, known }: columns added to the page later (not in `known`) keep their default.
 */
export default {
  props: {
    headers: Array,
    updateVisibleColumns: Function,
    prefKey: { type: String, default: "" },
  },
  data() {
    return {
      isMobile: false,
      open: false,
      // value -> visible, as the page set them up
      defaults: {},
      customized: false,
    };
  },
  computed: {
    userId() {
      const user = this.$store.getters.getUser;
      return user ? user.userId : null;
    },
    storageKey() {
      return `columns.${this.prefKey}`;
    },
  },
  watch: {
    // the page fills `headers` once it is mounted (its defaults set): then the saved choice goes on top
    headers: {
      immediate: true,
      handler(list) {
        if (list && list.length) this.applySaved(list);
      },
    },
    // the menu is placed once on opening; close it on scroll instead of leaving it behind the button
    open(isOpen) {
      if (isOpen) window.addEventListener("scroll", this.onScroll, true);
      else window.removeEventListener("scroll", this.onScroll, true);
    },
  },
  mounted() {
    this.isMobile = window.innerWidth < 600;
    window.addEventListener("resize", this.updateScreenWidth);
  },
  methods: {
    async applySaved(list) {
      this.defaults = Object.fromEntries(list.map((h) => [h.value, !!h.visible]));
      this.customized = false;
      if (!this.prefKey) return;
      const saved = await getPreference(this.userId, this.storageKey);
      if (list !== this.headers || !saved || !Array.isArray(saved.shown)) return;
      const shown = new Set(saved.shown);
      const known = new Set(Array.isArray(saved.known) ? saved.known : saved.shown);
      list.forEach((h) => {
        if (known.has(h.value)) h.visible = shown.has(h.value);
      });
      this.refresh();
    },
    changed() {
      this.refresh();
      if (!this.prefKey) return;
      savePreference(this.userId, this.storageKey, {
        shown: this.headers.filter((h) => h.visible).map((h) => h.value),
        known: this.headers.map((h) => h.value),
      });
    },
    reset() {
      this.headers.forEach((h) => {
        if (h.value in this.defaults) h.visible = this.defaults[h.value];
      });
      this.refresh();
      savePreference(this.userId, this.storageKey, null);
    },
    refresh() {
      this.customized = this.headers.some((h) => h.value in this.defaults && !!h.visible !== this.defaults[h.value]);
      this.updateVisibleColumns();
      // the pages add `visible` to their headers after they became reactive: redraw the checkboxes by hand
      this.$forceUpdate();
    },
    updateScreenWidth() {
      this.isMobile = window.innerWidth < 600;
    },
    onScroll(event) {
      const content = this.$refs.menu && this.$refs.menu.$refs.content;
      if (content && content.contains(event.target)) return;
      this.open = false;
    },
  },
  beforeDestroy() {
    window.removeEventListener("resize", this.updateScreenWidth);
    window.removeEventListener("scroll", this.onScroll, true);
  },
};
</script>

<style scoped>
.column-btn.v-btn {
  height: 44px !important;
  border-color: var(--line) !important;
  color: var(--ink-2) !important;
}
.columns-menu {
  background: #fff;
  padding: 14px 18px 16px;
}
.columns-menu__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 6px;
  min-height: 28px;
}
.columns-menu__title {
  font-weight: 700;
  color: var(--ink-2);
}
.columns-menu__reset.v-btn {
  margin-right: -8px;
}
.columns-menu__note {
  margin-top: 10px;
  max-width: 320px;
  font-size: 0.85rem;
  color: var(--ink-3);
}
.columns-checkboxes {
  display: grid;
  gap: 2px 18px;
  grid-template-columns: repeat(2, auto);
}
.columns-checkboxes ::v-deep .v-input--selection-controls {
  margin-top: 6px;
}
</style>
